import React, { useState, useEffect, useCallback } from 'react';
import api from './api';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2d2d2d' : '#f8f9fa',
        rowHover: isDark ? '#2a2a2a' : '#f5f5f5',
    };

    return {
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: `0 2px 5px ${colors.shadow}`,
        },
        header: {
            backgroundColor: colors.headerBg,
            color: colors.text,
            fontWeight: 'bold',
            padding: '12px 8px',
            textAlign: 'left',
            borderBottom: `2px solid ${colors.border}`,
            fontSize: '14px',
        },
        cell: {
            padding: '10px 8px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
            fontSize: '13px',
        },
        row: {
            '&:hover': {
                backgroundColor: colors.rowHover,
            },
        },
        container: {
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            marginBottom: '20px',
        },
        title: {
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '15px',
            color: colors.text,
        },
        description: {
            color: colors.subtleText,
            marginBottom: '15px',
            fontSize: '14px',
        },
        controls: {
            display: 'flex',
            gap: '10px',
            marginBottom: '15px',
            flexWrap: 'wrap',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
        },
        input: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
            minWidth: '200px',
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            backgroundColor: colors.link,
            color: 'white',
            '&:hover': {
                opacity: 0.9,
            },
        },
        stats: {
            display: 'flex',
            gap: '20px',
            marginBottom: '15px',
            flexWrap: 'wrap',
        },
        stat: {
            backgroundColor: colors.headerBg,
            padding: '10px 15px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
        },
        statLabel: {
            fontSize: '12px',
            color: colors.subtleText,
            marginBottom: '4px',
        },
        statValue: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: colors.text,
        },
        loading: {
            textAlign: 'center',
            padding: '20px',
            color: colors.subtleText,
        },
        error: {
            textAlign: 'center',
            padding: '20px',
            color: '#dc3545',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '15px',
        },
        noData: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        dateCell: {
            whiteSpace: 'nowrap',
        },
    };
};

function ACIReportTable({ theme, sites, onUpload }) {
    const styles = getStyles(theme);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSite, setSelectedSite] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('uploaded_at');
    const [sortDirection, setSortDirection] = useState('desc');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            let url = '/aci-reports/';
            const params = new URLSearchParams();
            
            if (selectedSite) {
                params.append('site', selectedSite);
            }
            
            if (sortField) {
                const ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
                params.append('ordering', ordering);
            }
            
            if (params.toString()) {
                url += `?${params.toString()}`;
            }
            
            const response = await api.get(url);
            // Ensure data is always an array
            setData(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching ACI Report data:', err);
            setError('Failed to fetch ACI Report data');
            setData([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    }, [selectedSite, sortField, sortDirection]);

    // Fetch data
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredData = (Array.isArray(data) ? data : []).filter(item => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            (item.buno && item.buno.toLowerCase().includes(searchLower)) ||
            (item.site_name && item.site_name.toLowerCase().includes(searchLower))
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return 'Invalid Date';
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return '↕️';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    // Calculate statistics
    const totalRecords = filteredData.length;
    const uniqueBunos = new Set(filteredData.map(item => item.buno).filter(Boolean)).size;
    const sitesWithData = new Set(filteredData.map(item => item.site_name).filter(Boolean)).size;

    if (loading) {
        return (
            <div style={styles.container}>
                <h3 style={styles.title}>ACI Report Data</h3>
                <div style={styles.loading}>Loading ACI Report data...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <h3 style={styles.title}>ACI Report Data</h3>
            <p style={styles.description}>
                View and manage ACI (Aircraft Condition Inspection) report data including BUNO and due dates.
            </p>

            {error && (
                <div style={styles.error}>
                    {error}
                </div>
            )}

            {/* Statistics */}
            <div style={styles.stats}>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Total Records</div>
                    <div style={styles.statValue}>{totalRecords}</div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Unique BUNOs</div>
                    <div style={styles.statValue}>{uniqueBunos}</div>
                </div>
                <div style={styles.stat}>
                    <div style={styles.statLabel}>Sites</div>
                    <div style={styles.statValue}>{sitesWithData}</div>
                </div>
            </div>

            {/* Controls */}
            <div style={styles.controls}>
                <select
                    style={styles.select}
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                >
                    <option value="">All Sites</option>
                    {sites.map(site => (
                        <option key={site.id} value={site.id}>
                            {site.name}
                        </option>
                    ))}
                </select>

                <input
                    style={styles.input}
                    type="text"
                    placeholder="Search by BUNO or Site..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <button
                    style={styles.button}
                    onClick={fetchData}
                >
                    Refresh
                </button>
            </div>

            {/* Table */}
            {filteredData.length === 0 ? (
                <div style={styles.noData}>
                    No ACI Report data found. Upload a report to get started.
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th 
                                    style={styles.header}
                                    onClick={() => handleSort('buno')}
                                >
                                    BUNO {getSortIcon('buno')}
                                </th>
                                <th 
                                    style={styles.header}
                                    onClick={() => handleSort('site_name')}
                                >
                                    Site {getSortIcon('site_name')}
                                </th>
                                <th 
                                    style={styles.header}
                                    onClick={() => handleSort('aci_due_date')}
                                >
                                    ACI Due Date {getSortIcon('aci_due_date')}
                                </th>
                                <th 
                                    style={styles.header}
                                    onClick={() => handleSort('uploaded_at')}
                                >
                                    Uploaded At {getSortIcon('uploaded_at')}
                                </th>
                                <th style={styles.header}>Uploaded By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item, index) => (
                                <tr key={item.id || index} style={styles.row}>
                                    <td style={styles.cell}>
                                        {item.buno || 'N/A'}
                                    </td>
                                    <td style={styles.cell}>
                                        {item.site_name || 'N/A'}
                                    </td>
                                    <td style={{...styles.cell, ...styles.dateCell}}>
                                        {formatDate(item.aci_due_date)}
                                    </td>
                                    <td style={{...styles.cell, ...styles.dateCell}}>
                                        {formatDate(item.uploaded_at)}
                                    </td>
                                    <td style={styles.cell}>
                                        {item.uploaded_by || 'N/A'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default ACIReportTable;